import { useMemo } from 'react';
import { Select } from '@customerio/pluma-components/react';
import { objectTypeIcon, type ObjectType } from '@/services/db/object-type/entity';
import { encodeScopeSelection, PROFILE_SELECTION } from '../urls';

export type ObjectTypeSelectProps = {
  value: string;
  objectTypes: readonly ObjectType[];
  onChange: (value: string) => void;
};

/**
 * Which attributes the list is showing: a person's, or one object type's own
 * attributes or its relationship attributes.
 *
 * Each object type is a group of two, because an object and its relationships
 * carry separate attributes that happen to share a name.
 */
export function ObjectTypeSelect({ value, objectTypes, onChange }: ObjectTypeSelectProps) {
  const options = useMemo(
    () => [
      { label: 'Profile Attributes', value: PROFILE_SELECTION, icon: 'person' as const },
      ...objectTypes.map((objectType, index) => {
        const icon = objectTypeIcon(objectType);
        return {
          label: objectType.name,
          isCollapsible: true,
          // The first group is open, so a workspace with one object type does
          // not have to expand anything to reach it.
          defaultIsCollapsed: index > 0,
          options: [
            {
              label: `${objectType.name} Attributes`,
              value: encodeScopeSelection({ kind: 'object', objectTypeId: Number(objectType.id) }),
              icon,
            },
            {
              label: `${objectType.name} Relationship Attributes`,
              value: encodeScopeSelection({ kind: 'relationship', objectTypeId: Number(objectType.id) }),
              icon,
            },
          ],
        };
      }),
    ],
    [objectTypes],
  );

  return (
    <Select
      value={value}
      onChange={(next) => onChange(String(next))}
      options={options}
      ariaLabel="Select object type"
      maxW="full"
      style={{ maxWidth: '324px' }}
    />
  );
}
